<template>
    <!-- Conversation Modal -->
    <ConversationViewModal
        v-if="modals.conversation_view.is_open"
        :open="modals.conversation_view.is_open"
        :conversation="modals.conversation_view.conversation"
        @close="$store.commit('modal:close', 'conversation_view')"
        @reload="reloadConversations"
        @reply="handleReply"
    />

    <!-- Compose Modal -->
    <EmailComposeModal
        v-if="modals.email_compose.is_open"
        :open="modals.email_compose.is_open"
        :reply-to-message="modals.email_compose.replyToMessage || null"
        :reply-to-conversation="modals.email_compose.replyToConversation || null"
        :prefilled-recipient="modals.email_compose.recipient || ''"
        :prefilled-recipients="modals.email_compose.recipients || []"
        :prefilled-subject="modals.email_compose.subject || ''"
        @close="$store.commit('modal:close', 'email_compose')"
        @sent="onEmailSent"
    />

    <UIDialog :open="deleteModal.isOpen" @update:open="onDeleteModalOpenChange">
        <DialogContent class="max-w-md">
            <DialogHeader>
                <DialogTitle>{{ deleteModalTitle }}</DialogTitle>
                <DialogDescription>{{ deleteModalDescription }}</DialogDescription>
            </DialogHeader>

            <DialogFooter>
                <Button variant="outline" :disabled="deleteModal.isDeleting" @click="resetDeleteModal">
                    Cancel
                </Button>
                <Button variant="destructive" :disabled="deleteModal.isDeleting" @click="confirmDeleteConversations">
                    <AppIcon
                        v-if="deleteModal.isDeleting"
                        name="spinner-third"
                        class="mr-2 h-3 w-3 animate-spin"
                    />
                    Delete
                </Button>
            </DialogFooter>
        </DialogContent>
    </UIDialog>

    <div>
        <div class="col-span-full mt-4 rounded-sm border border-slate-200 bg-white shadow-lg">
            <div class="p-3">
                <div class="pb-3">
                    <DataTableToolbar
                        :table="toolbarTable"
                        placeholder="Search messages..."
                        column-to-filter="search"
                        :is-server-side="true"
                        :hide-view-options="true"
                        @update:input="onSearchInput"
                    >
                        <template #toolbar-end>
                            <DropdownMenu>
                                <DropdownMenuTrigger as-child>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        class="h-8 w-8 shrink-0 border-slate-200 p-0 text-slate-500 hover:bg-slate-50"
                                        aria-label="Filter messages"
                                    >
                                        <AppIcon name="filter" class="h-3 w-3" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" class="w-40">
                                    <DropdownMenuItem
                                        v-for="option in readStatusOptions"
                                        :key="option.value"
                                        class="cursor-pointer gap-2"
                                        @click="setReadStatus(option.value)"
                                    >
                                        <AppIcon :name="option.icon" class="h-3.5 w-3.5 text-muted-foreground/70" />
                                        <span>{{ getReadStatusOptionLabel(option) }}</span>
                                        <AppIcon
                                            v-if="filters.read_status === option.value"
                                            name="check"
                                            class="ml-auto h-3 w-3 text-indigo-600"
                                        />
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                            <DropdownMenu v-if="selectedConversations.length">
                                <DropdownMenuTrigger as-child>
                                    <Button type="button" variant="outline" size="sm" class="h-8 gap-1.5" :disabled="isBusy">
                                        <AppIcon name="ellipsis" class="h-3.5 w-3.5 text-slate-500" />
                                        {{ selectedConversations.length }} selected
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" class="w-48">
                                    <DropdownMenuItem
                                        class="cursor-pointer gap-2"
                                        :disabled="isBusy"
                                        @click="onBulkMarkRead"
                                    >
                                        <AppIcon
                                            name="envelope-open-text"
                                            class="h-3.5 w-3.5 text-slate-600"
                                        />
                                        <span>Mark as read</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        class="cursor-pointer gap-2"
                                        :disabled="isBusy"
                                        @click="onBulkArchive"
                                    >
                                        <AppIcon
                                            name="archive"
                                            class="h-3.5 w-3.5 text-slate-600"
                                        />
                                        <span>Archive</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        class="cursor-pointer gap-2 text-red-600 focus:text-red-600"
                                        :disabled="isBusy"
                                        @click="onBulkDelete"
                                    >
                                        <AppIcon name="trash" class="h-3.5 w-3.5" />
                                        <span>Delete</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </template>
                    </DataTableToolbar>
                </div>

                <div
                    class="relative w-full overflow-auto rounded-sm scrollbar scrollbar-track-slate-200 scrollbar-thumb-slate-300 scrollbar-corner-slate-100 scrollbar-w-2 scrollbar-h-2 hover:scrollbar-thumb-slate-400 active:scrollbar-thumb-slate-500"
                >
                    <table v-if="!isLoading && conversations.length" class="w-full table-auto">
                        <thead class="rounded-sm bg-slate-50 text-xs uppercase text-slate-400 *:border-none">
                            <tr class="border-none">
                                <th class="w-7 whitespace-nowrap py-3 pl-4 pr-0 text-left font-semibold uppercase">
                                    <input
                                        type="checkbox"
                                        class="form-checkbox"
                                        :checked="allVisibleSelected"
                                        :disabled="conversations.length === 0"
                                        :indeterminate.prop="partiallySelected"
                                        @change="onToggleSelectAll"
                                    />
                                </th>
                                <th class="whitespace-nowrap py-3 text-left font-semibold uppercase">
                                    <DataTableSortableColumnHeader :column="getSortableColumn('subject')" title="Subject" />
                                </th>
                                <th class="whitespace-nowrap py-3 text-left font-semibold uppercase">
                                    <DataTableSortableColumnHeader
                                        :column="getSortableColumn('creator_email')"
                                        title="Creator Email"
                                    />
                                </th>
                                <th class="whitespace-nowrap px-3 py-3 text-left font-semibold uppercase">
                                    Initiated By
                                </th>
                                <th class="whitespace-nowrap py-3 text-left font-semibold uppercase">
                                    <DataTableSortableColumnHeader
                                        :column="getSortableColumn('message_count')"
                                        title="Messages"
                                    />
                                </th>
                                <th class="whitespace-nowrap py-3 text-left font-semibold uppercase">
                                    <DataTableSortableColumnHeader
                                        :column="getSortableColumn('last_message_at')"
                                        title="Last Message"
                                    />
                                </th>
                                <th class="w-8 whitespace-nowrap py-3"></th>
                            </tr>
                        </thead>

                        <tbody
                            class="divide-y divide-slate-200 border-b border-slate-200 text-sm"
                            :class="{ 'pointer-events-none opacity-50': isBusy }"
                        >
                            <tr
                                v-for="conversation in conversations"
                                :key="conversation.id"
                                class="group hover:bg-slate-50"
                                :class="{ 'unread-row bg-slate-50': conversation.is_unread }"
                            >
                                <td class="whitespace-nowrap px-2 py-3.5 pl-3">
                                    <input
                                        type="checkbox"
                                        class="form-checkbox"
                                        :checked="isConversationSelected(conversation)"
                                        @change="onToggleConversation(conversation, $event)"
                                    />
                                </td>

                                <td class="whitespace-nowrap px-2 py-3.5">
                                    <div class="relative flex items-center">
                                        <div class="max-w-xs">
                                            <div class="font-medium text-slate-900">{{ conversation.subject }}</div>
                                            <div class="mt-1 text-xs text-slate-500">
                                                {{ conversation.participants?.length || 0 }} participants
                                            </div>
                                        </div>

                                        <div
                                            class="absolute right-0 top-0 hidden h-full cursor-pointer rounded bg-slate-100 px-2 text-xs font-semibold transition duration-200 group-hover:flex hover:bg-slate-200"
                                            @click.stop="openConversationModal(conversation)"
                                        >
                                            <span class="my-auto flex uppercase">
                                                <AppIcon
                                                    name="rectangle-list"
                                                    class="my-auto mr-1 h-4 w-4"
                                                />
                                                <span class="my-auto leading-none">Open</span>
                                            </span>
                                        </div>
                                    </div>
                                </td>

                                <td class="px-2 py-3.5">
                                    <div>
                                        <div class="font-medium">{{ conversation.creator_email }}</div>
                                        <div v-if="conversation.campaign" class="text-xs text-indigo-600">
                                            Campaign linked
                                        </div>
                                    </div>
                                </td>

                                <td class="whitespace-nowrap px-2 py-3.5">
                                    <div class="flex grow items-center">
                                        <Avatar
                                            class="bg-slate-700 text-slate-50"
                                            :username="
                                                `${conversation.email_account?.operator?.first_name || ''} ${conversation.email_account?.operator?.last_name || ''}`.trim()
                                            "
                                            :size="28"
                                        />
                                        <div class="flex flex-col pl-2">
                                            <div class="pr-2 font-semibold">
                                                <span>
                                                    {{ conversation.email_account?.operator?.first_name }}
                                                    {{ conversation.email_account?.operator?.last_name }}
                                                </span>
                                            </div>
                                            <div class="text-xs text-slate-500">
                                                {{ conversation.email_account?.email_address }}
                                            </div>
                                        </div>
                                    </div>
                                </td>

                                <td class="px-2 py-3.5">
                                    <Badge variant="secondary">{{ conversation.message_count }}</Badge>
                                </td>

                                <td class="px-2 py-3.5">
                                    {{ formatLastMessagedAt(conversation.last_message_at) }}
                                </td>

                                <td class="w-8 px-2 text-right">
                                    <ConversationActions
                                        :conversation="conversation"
                                        @mark-read="onMarkRead"
                                        @archive="onArchiveConversation"
                                        @unarchive="onUnarchiveConversation"
                                        @delete="onDeleteConversation"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div v-if="isLoading" class="w-full p-4 text-center">
                        <p class="text-sm font-medium">Loading messages...</p>
                    </div>

                    <div v-else-if="!conversations.length" class="w-full p-4 text-center">
                        <p class="text-sm font-medium">No messages to show</p>
                    </div>
                </div>
            </div>

            <div class="mx-4 my-4">
                <DiscoveryPagination :paging="paging" record-name="messages" @paging-changed="onPagingChange" />
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import DataTableSortableColumnHeader from '@/components/ui/data-table/DataTableSortableColumnHeader.vue'
import DataTableToolbar from '@/components/ui/data-table/DataTableToolbar.vue'
import DiscoveryPagination from '@/partials/tables/discovery/DiscoveryPagination.vue'
import ConversationActions from '@/components/ConversationActions.vue'
import ConversationViewModal from '@/partials/modals/ConversationViewModal.vue'
import EmailComposeModal from '@/partials/modals/EmailComposeModal.vue'
import Avatar from '@/components/Avatar.vue'
import emailAPI from '@/utils/api/api.email'

export default {
    name: 'EmailConversationsTable',

    components: {
        Badge,
        Button,
        UIDialog: Dialog,
        DialogContent,
        DialogDescription,
        DialogFooter,
        DialogHeader,
        DialogTitle,
        DropdownMenu,
        DropdownMenuContent,
        DropdownMenuItem,
        DropdownMenuSeparator,
        DropdownMenuTrigger,
        DataTableSortableColumnHeader,
        DataTableToolbar,
        DiscoveryPagination,
        ConversationActions,
        ConversationViewModal,
        EmailComposeModal,
        Avatar,
    },

    props: {
        activeSubtab: {
            type: String,
            default: null,
        },
    },

    emits: ['render-subtabs'],

    data() {
        return {
            isLoading: false,
            isBusy: false,
            activeTab: 'active',
            filters: {
                status: 'active',
                read_status: 'all',
                search: '',
            },
            paging: {
                currentPage: 1,
                totalRecords: 0,
                totalPages: 1,
                perPage: 25,
                sortby: 'last_message_at',
                sortdir: 'desc',
            },
            conversations: [],
            summary: {
                active: 0,
                archived: 0,
                snoozed: 0,
                active_read: 0,
                active_unread: 0,
                archived_read: 0,
                archived_unread: 0,
            },
            tabs: [
                { key: 'active', label: 'Active' },
                { key: 'archived', label: 'Archived' },
            ],
            readStatusOptions: [
                { value: 'all', label: 'All', icon: 'rectangle-list' },
                { value: 'unread', label: 'Unread', icon: 'envelope' },
                { value: 'read', label: 'Read', icon: 'envelope-open' },
            ],
            selectedConversations: [],
            searchDebounce: null,
            deleteModal: {
                isOpen: false,
                isDeleting: false,
                isBulk: false,
                conversations: [],
            },
            isComposeModalOpen: false,
            replyToMessage: null,
            replyToConversation: null,
            composeData: {
                recipient: null,
                subject: null,
                replyToMessage: null,
                replyToConversation: null,
            },
        }
    },

    computed: {
        ...mapGetters(['selfActiveOrganizationId', 'modals']),

        selectedConversationIds() {
            return this.selectedConversations.map((conversation) => conversation.id)
        },

        allVisibleSelected() {
            return (
                this.conversations.length > 0 &&
                this.conversations.every((conversation) => this.selectedConversationIds.includes(conversation.id))
            )
        },

        partiallySelected() {
            return this.selectedConversations.length > 0 && !this.allVisibleSelected
        },

        deleteModalTitle() {
            return this.deleteModal.conversations.length === 1 ? 'Delete Message' : 'Delete Messages'
        },

        deleteModalDescription() {
            if (this.deleteModal.conversations.length === 1) {
                return 'Are you sure you want to delete this message? This action cannot be undone.'
            }

            return `Are you sure you want to delete ${this.deleteModal.conversations.length} messages? This action cannot be undone.`
        },

        toolbarTable() {
            const vm = this

            return {
                getState() {
                    const columnFilters = []

                    if (vm.filters.search?.trim()) {
                        columnFilters.push({ id: 'search', value: vm.filters.search })
                    }

                    if (vm.filters.read_status !== 'all') {
                        columnFilters.push({ id: 'read_status', value: vm.filters.read_status })
                    }

                    return { columnFilters }
                },
                getColumn(columnId) {
                    return {
                        getFilterValue() {
                            if (columnId === 'search') return vm.filters.search
                            if (columnId === 'read_status') return vm.filters.read_status
                            return ''
                        },
                        setFilterValue(value) {
                            if (columnId === 'search') vm.onSearchInput(value)
                            if (columnId === 'read_status') vm.setReadStatus(value || 'all')
                        },
                    }
                },
                resetColumnFilters() {
                    vm.resetFilters()
                },
            }
        },
    },

    mounted() {
        this.emitSubtabs()
    },

    created() {
        this.reloadConversations()
    },

    beforeUnmount() {
        if (this.searchDebounce) {
            clearTimeout(this.searchDebounce)
        }
    },

    watch: {
        activeSubtab(newVal) {
            if (newVal && newVal !== this.activeTab) {
                this.setActiveTab(newVal)
            }
        },
    },

    methods: {
        emitSubtabs() {
            this.$emit(
                'render-subtabs',
                this.tabs.map((tab) => ({
                    key: tab.key,
                    label: tab.label,
                    count: this.getTabRecordCount(tab.key),
                })),
                this.activeTab,
            )
        },

        async reloadConversations() {
            await Promise.all([this.getConversations(), this.getConversationStats()])
            this.emitSubtabs()
        },

        async onEmailSent() {
            this.$store.commit('modal:close', 'email_compose')

            await this.reloadConversations()
        },

        getSortableColumn(column) {
            const vm = this

            return {
                getCanSort() {
                    return true
                },
                getCanHide() {
                    return false
                },
                getIsSorted() {
                    return vm.paging.sortby === column ? vm.paging.sortdir : false
                },
                getIsVisible() {
                    return true
                },
                toggleSorting(desc) {
                    vm.setSort(column, desc ? 'desc' : 'asc')
                },
                toggleVisibility() {},
            }
        },

        async setSort(column, direction) {
            this.paging.sortby = column
            this.paging.sortdir = direction
            await this.getConversations()
        },

        setActiveTab(tab) {
            this.isLoading = true
            this.activeTab = tab
            this.filters.status = tab
            this.paging.currentPage = 1
            this.clearSelection()
            this.getConversations().then(() => {
                this.emitSubtabs()
            })
        },

        getTabRecordCount(key) {
            return this.summary[key] || 0
        },

        getRequestFilters() {
            const filters = {
                ...this.filters,
                search: this.filters.search?.trim() || undefined,
            }

            if (filters.read_status === 'all') {
                delete filters.read_status
            }

            return filters
        },

        async getConversations() {
            this.isLoading = true
            const res = await emailAPI.getConversations(
                this.$axios,
                this.selfActiveOrganizationId,
                this.getRequestFilters(),
                this.paging,
            )

            if (res.success) {
                this.paging.totalRecords = res.value.paging.totalRecords
                this.paging.totalPages = Math.max(1, res.value.paging.totalPages || Math.ceil(this.paging.totalRecords / this.paging.perPage))
                this.conversations = res.value.records
                this.clearSelection()
            } else {
                this.$errorHandler('Failed to load messages')
            }

            this.isLoading = false
            this.emitSubtabs()
        },

        async getConversationStats() {
            const res = await emailAPI.getConversationStats(this.$axios)
            if (res.success) this.summary = res.value.stats || {}
        },

        getReadStatusCount(readStatus) {
            if (!['read', 'unread'].includes(readStatus)) {
                return null
            }

            const count = Number(this.summary?.[`${this.filters.status}_${readStatus}`])

            return Number.isFinite(count) ? count : null
        },

        getReadStatusOptionLabel(option) {
            const count = this.getReadStatusCount(option.value)

            return count === null ? option.label : `${option.label} (${count.toLocaleString()})`
        },

        onSearchInput(value) {
            this.filters.search = value

            if (this.searchDebounce) {
                clearTimeout(this.searchDebounce)
            }

            this.searchDebounce = setTimeout(() => {
                this.searchDebounce = null
                this.paging.currentPage = 1
                this.getConversations()
            }, 300)
        },

        onFilterChange() {
            if (this.searchDebounce) {
                clearTimeout(this.searchDebounce)
                this.searchDebounce = null
            }

            this.paging.currentPage = 1
            this.getConversations()
        },

        setReadStatus(readStatus) {
            if (this.filters.read_status === readStatus) return

            this.filters.read_status = readStatus
            this.onFilterChange()
        },

        resetFilters() {
            this.filters.search = ''
            this.filters.read_status = 'all'
            this.onFilterChange()
        },

        clearSearch() {
            this.filters.search = ''
            this.onFilterChange()
        },

        onPagingChange(paging) {
            Object.assign(this.paging, paging)
            this.getConversations()
        },

        onToggleSelectAll(event) {
            this.selectedConversations = event.target.checked ? [...this.conversations] : []
        },

        onToggleConversation(conversation, event) {
            if (event.target.checked) {
                if (!this.selectedConversationIds.includes(conversation.id)) {
                    this.selectedConversations = [...this.selectedConversations, conversation]
                }
                return
            }

            this.selectedConversations = this.selectedConversations.filter((item) => item.id !== conversation.id)
        },

        isConversationSelected(conversation) {
            return this.selectedConversationIds.includes(conversation.id)
        },

        clearSelection() {
            this.selectedConversations = []
        },

        async onBulkMarkRead() {
            await this.performBulkAction('mark_read', 'Messages marked as read')
        },

        async onBulkArchive() {
            await this.performBulkAction('archive', 'Messages archived')
        },

        onBulkDelete() {
            this.openDeleteConfirmation(this.selectedConversations, true)
        },

        async performBulkAction(action, successMessage) {
            if (!this.selectedConversationIds.length) {
                return
            }

            this.isBusy = true
            const res = await emailAPI.bulkUpdateConversations(this.$axios, this.selectedConversationIds, action)

            if (res.success) {
                this.$successHandler(successMessage)
                await this.reloadConversations()
            } else {
                this.$errorHandler('Failed to update messages')
            }

            this.isBusy = false
        },

        async onArchiveConversation(conversation) {
            const res = await emailAPI.archiveConversation(this.$axios, conversation.id)
            if (res.success) {
                this.$successHandler('Message archived')
                this.reloadConversations()
            } else this.$errorHandler('Failed to archive')
        },

        async onUnarchiveConversation(conversation) {
            const res = await emailAPI.unarchiveConversation(this.$axios, conversation.id)
            if (res.success) {
                this.$successHandler('Message unarchived')
                this.reloadConversations()
            } else this.$errorHandler('Failed to unarchive')
        },

        async onMarkRead(conversation) {
            const res = await emailAPI.markConversationAsRead(this.$axios, conversation.id)
            if (res.success) {
                this.$successHandler('Marked as read')
                this.reloadConversations()
            } else this.$errorHandler('Failed to mark read')
        },

        onDeleteConversation(conversation) {
            this.openDeleteConfirmation([conversation], false)
        },

        openDeleteConfirmation(conversations, isBulk) {
            if (!conversations.length) {
                return
            }

            this.deleteModal = {
                isOpen: true,
                isDeleting: false,
                isBulk,
                conversations: [...conversations],
            }
        },

        onDeleteModalOpenChange(isOpen) {
            if (this.deleteModal.isDeleting) {
                return
            }

            if (!isOpen) {
                this.resetDeleteModal()
                return
            }

            this.deleteModal.isOpen = true
        },

        resetDeleteModal() {
            this.deleteModal = {
                isOpen: false,
                isDeleting: false,
                isBulk: false,
                conversations: [],
            }
        },

        async confirmDeleteConversations() {
            const conversations = this.deleteModal.conversations

            if (!conversations.length) {
                return
            }

            this.deleteModal.isDeleting = true
            const conversationIds = conversations.map((conversation) => conversation.id)
            const res =
                conversations.length === 1 && !this.deleteModal.isBulk
                    ? await emailAPI.deleteConversation(this.$axios, conversationIds[0])
                    : await emailAPI.bulkUpdateConversations(this.$axios, conversationIds, 'delete')

            if (res.success) {
                this.$successHandler(
                    conversations.length === 1 ? 'Message deleted' : 'Messages deleted',
                )
                this.resetDeleteModal()
                await this.reloadConversations()
            } else {
                this.$errorHandler('Failed to delete messages')
                this.deleteModal.isDeleting = false
            }
        },

        async openConversationModal(conversation) {
            // Mark as read if needed
            if (conversation.is_unread) {
                try {
                    const res = await emailAPI.markConversationAsRead(this.$axios, conversation.id)
                    if (res.success) {
                        // Update local for responsiveness
                        conversation.is_unread = false
                        await this.getConversationStats()
                        this.emitSubtabs()

                        if (this.filters.read_status === 'unread') {
                            await this.getConversations()
                        }
                    }
                } catch (err) {
                    console.error('Failed to mark as read on open:', err)
                }
            }

            this.$store.commit('modal:setData', {
                modal_id: 'conversation_view',
                data: {
                    is_open: true,
                    conversation,
                },
            })
        },

        async handleReply(replyData) {
            this.$store.commit('modal:close', 'conversation_view')

            const composeData = {
                is_open: true,
                recipient: null,
                subject: null,
                replyToMessage: null,
                replyToConversation: null,
            }

            if (replyData.type === 'message') {
                composeData.replyToMessage = replyData.message
                composeData.replyToConversation = replyData.conversation
                composeData.subject = replyData.conversation?.subject ? `Re: ${replyData.conversation.subject}` : 'Re:'
                composeData.recipient = replyData.message?.from_email || null
            } else if (replyData.type === 'conversation') {
                composeData.replyToConversation = replyData.conversation
                composeData.subject = replyData.conversation?.subject ? `Re: ${replyData.conversation.subject}` : 'Re:'
                composeData.recipient = replyData.conversation?.creator_email || null
            }

            await this.$nextTick()

            this.$store.commit('modal:open', 'email_compose')

            this.$store.commit('modal:setData', {
                modal_id: 'email_compose',
                data: composeData,
            })

            this.$emit('reload')
        },

        openComposeModal(prefilledRecipient = null) {
            this.$store.commit('modal:setData', {
                modal_id: 'email_compose',
                data: {
                    is_open: true,
                    recipient: prefilledRecipient,
                    subject: null,
                    replyToMessage: null,
                    replyToConversation: null,
                },
            })
        },

        formatLastMessagedAt(dateString) {
            if (!dateString) {
                return ''
            }
            const date = new Date(dateString)
            const now = new Date()

            const isToday = date.toDateString() === now.toDateString()
            const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))

            if (isToday) {
                // Example: 2:45 PM
                return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })
            } else if (diffDays < 7) {
                // Example: Mon
                return date.toLocaleDateString([], { weekday: 'short' })
            } else if (date.getFullYear() === now.getFullYear()) {
                // Example Oct 5
                return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
            } else {
                // Example Oct 5 2024
                return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
            }
        },
    },
}
</script>

<style scoped>
.unread-row {
    font-weight: 600 !important;
    color: #0f172a !important; /* slate-900 */
}

.unread-row td * {
    font-weight: 600 !important;
    color: #0f172a !important; /* slate-900 */
}

.unread-row .vue-avatar--wrapper * {
    color: #f8fafc !important; /* slate-50 */
}
</style>
